import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Optional,
  Output,
  SimpleChanges
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import {
  QC_PERIODS,
  QcError,
  QcErrorType,
  QcEvent,
  QcOutcomeOption,
  QcRecord
} from '../../domain/qc.model';
import { AlertService } from '../../services/alert.service';
import { QcService } from '../../services/qc.service';

type QcObligationKey = keyof QcRecord['postQcObligations'];

@Component({
  selector: 'app-qc-expanded-panel',
  templateUrl: './qc-expanded-panel.component.html',
  styleUrls: ['./qc-expanded-panel.component.css']
})
export class QcExpandedPanelComponent implements OnChanges {
  readonly errorColumns = [
    'index',
    'period',
    'gameTime',
    'errorType',
    'collector',
    'qc',
    'comment',
    'actions'
  ];
  @Input() event!: QcEvent;
  @Output() qcSaved = new EventEmitter<QcRecord>();

  readonly periods = QC_PERIODS;
  readonly outcomeOptions: QcOutcomeOption[] = [
    'delay',
    'missing shift',
    'plus minus'
  ];
  readonly errorTypeOptions: Array<{ value: QcErrorType; label: string }> = [
    { value: 'outcome', label: 'outcome' },
    { value: 'data_missing', label: 'data missing' },
    { value: 'data_surplus', label: 'data surplus' },
    { value: 'data_incomplete', label: 'data incomplete' },
    { value: 'generic', label: 'generic' }
  ];

  qc: QcRecord = this.createEmptyQc();
  hasPendingChanges = false;

  constructor(
    private qcService: QcService,
    private alertService: AlertService,
    @Optional() private dialogRef?: MatDialogRef<unknown>
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.event) {
      return;
    }
    this.qc = this.cloneQc(this.event?.qc);
    this.hasPendingChanges = false;
  }

  get addedErrorsCount(): number {
    return this.qc.errors.filter((row) => this.isRowValid(row)).length;
  }

  get errorPercentage(): number | null {
    if (!this.hasValidTotalEvents) {
      return null;
    }
    return (this.addedErrorsCount / (this.qc.totalEvents ?? 0)) * 100;
  }

  get accuracyPercentage(): number | null {
    if (this.errorPercentage === null) {
      return null;
    }
    return Math.max(0, 100 - this.errorPercentage);
  }

  get hasValidTotalEvents(): boolean {
    return typeof this.qc.totalEvents === 'number' && this.qc.totalEvents > 0;
  }

  get hasInvalidStartedRows(): boolean {
    return this.qc.errors.some(
      (row) => this.isRowStarted(row) && !this.isRowValid(row)
    );
  }

  get isDone(): boolean {
    return this.qc.status === 'done';
  }

  onTotalEventsChanged(value: number | string | null): void {
    this.qc.totalEvents = this.normalizeTotalEvents(value);
    this.markPendingChanges();
  }

  onErrorTypeChanged(row: QcError, nextType: QcError['errorType']): void {
    row.errorType = nextType;
    row.collector = null;
    row.qc = null;
    row.comment = null;
    this.markPendingChanges();
  }

  addError(): void {
    this.qc = {
      ...this.qc,
      errors: [...this.qc.errors, this.createEmptyError()]
    };
    this.markPendingChanges();
  }

  removeError(index: number): void {
    this.qc = {
      ...this.qc,
      errors: this.qc.errors.filter((_, rowIndex) => rowIndex !== index)
    };
    this.markPendingChanges();
  }

  onFieldChanged(): void {
    this.markPendingChanges();
  }

  onGameTimeChanged(row: QcError, value: string): void {
    row.gameTime = this.formatGameTime(value);
    this.markPendingChanges();
  }

  onQcTextChanged(row: QcError, value: string | null): void {
    row.qc = this.normalizeNullableText(value);
    this.markPendingChanges();
  }

  onCommentChanged(row: QcError, value: string | null): void {
    row.comment = this.normalizeNullableText(value);
    this.markPendingChanges();
  }

  onObligationChanged(key: QcObligationKey, checked: boolean): void {
    this.qc.postQcObligations[key] = checked;
    this.markPendingChanges();
  }

  async saveChanges(): Promise<void> {
    if (!this.hasPendingChanges) {
      return;
    }

    try {
      await this.saveCurrentQc(this.qc);
      this.hasPendingChanges = false;
    } catch (error) {
      this.alertService.showError(
        'Could not save QC data: ' + (error?.message ?? 'unknown error')
      );
    }
  }

  async saveAndClose(): Promise<void> {
    if (this.hasPendingChanges) {
      try {
        await this.saveCurrentQc(this.qc);
        this.hasPendingChanges = false;
      } catch (error) {
        this.alertService.showError(
          'Could not save QC data: ' + (error?.message ?? 'unknown error')
        );
        return;
      }
    }

    this.dialogRef?.close();
  }

  async toggleDone(): Promise<void> {
    if (this.isDone) {
      this.qc = {
        ...this.qc,
        status: null
      };
      this.markPendingChanges();
      await this.saveChanges();
      return;
    }

    if (!this.hasValidTotalEvents) {
      this.alertService.showError(
        'Total Events must be greater than 0 to complete QC.'
      );
      return;
    }

    if (this.hasInvalidStartedRows) {
      this.alertService.showError(
        'Complete or clear invalid QC error rows before marking as done.'
      );
      return;
    }

    this.qc = {
      ...this.qc,
      status: 'done'
    };
    this.markPendingChanges();
    await this.saveChanges();
  }

  shouldShowCollectorSelect(row: QcError): boolean {
    return row.errorType === 'outcome';
  }

  shouldShowQcSelect(row: QcError): boolean {
    return row.errorType === 'outcome';
  }

  shouldShowQcTextInput(row: QcError): boolean {
    return (
      row.errorType === 'data_missing' ||
      row.errorType === 'data_surplus' ||
      row.errorType === 'data_incomplete'
    );
  }

  shouldShowComment(row: QcError): boolean {
    return row.errorType === 'generic';
  }

  getQcPlaceholder(row: QcError): string {
    switch (row.errorType) {
      case 'data_missing':
        return 'What is missing?';
      case 'data_surplus':
        return 'What is surplus?';
      case 'data_incomplete':
        return 'What is incomplete?';
      default:
        return '';
    }
  }

  isGameTimeInvalid(row: QcError): boolean {
    return !!row.gameTime && !/^\d{2}:\d{2}$/.test(row.gameTime);
  }

  isRowStarted(row: QcError): boolean {
    return Boolean(
      row.gameTime || row.errorType || row.collector || row.qc || row.comment
    );
  }

  isRowValid(row: QcError): boolean {
    if (!row.errorType) {
      return false;
    }
    if (this.isGameTimeInvalid(row)) {
      return false;
    }

    switch (row.errorType) {
      case 'generic':
        return this.hasNonBlankText(row.comment);
      case 'outcome':
        return !!row.collector && !!row.qc;
      case 'data_missing':
      case 'data_surplus':
      case 'data_incomplete':
        return this.hasNonBlankText(row.qc);
      default:
        return false;
    }
  }

  private markPendingChanges(): void {
    this.hasPendingChanges = true;
  }

  private async saveCurrentQc(nextQc: QcRecord = this.qc): Promise<void> {
    const eventId = this.event._id;
    const normalizedQc = this.normalizeQcForSave(nextQc);

    const response = await firstValueFrom(
      this.qcService.updateQcData(eventId, normalizedQc)
    );
    this.qc = this.cloneQc(response.qc);
    this.qcSaved.emit(this.qc);
  }

  private normalizeTotalEvents(value: number | string | null): number | null {
    if (value === null || value === '') {
      return null;
    }

    const normalized = typeof value === 'number' ? value : parseInt(value, 10);
    return Number.isFinite(normalized) ? normalized : null;
  }

  private formatGameTime(value: string | null | undefined): string | null {
    const digits = (value ?? '').replace(/\D/g, '').slice(0, 4);
    if (!digits) {
      return null;
    }

    if (digits.length <= 2) {
      return digits;
    }

    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  }

  private cloneQc(qc: QcRecord | null | undefined): QcRecord {
    const errors = (qc?.errors ?? []).map((row) => this.normalizeError(row));

    return {
      totalEvents: qc?.totalEvents ?? null,
      errors,
      postQcObligations: {
        correction: qc?.postQcObligations?.correction ?? false,
        qaMeeting: qc?.postQcObligations?.qaMeeting ?? false,
        reCollection: qc?.postQcObligations?.reCollection ?? false
      },
      status: qc?.status ?? null
    };
  }

  private createEmptyQc(): QcRecord {
    return {
      totalEvents: null,
      errors: [],
      postQcObligations: {
        correction: false,
        qaMeeting: false,
        reCollection: false
      },
      status: null
    };
  }

  private createEmptyError(): QcError {
    return {
      period: null,
      gameTime: null,
      errorType: null,
      collector: null,
      qc: null,
      comment: null
    };
  }

  private normalizeQcForSave(qc: QcRecord): QcRecord {
    return {
      ...qc,
      errors: qc.errors.map((row) => this.normalizeError(row))
    };
  }

  private normalizeError(row: QcError): QcError {
    return {
      period: this.normalizeNullableOption(row.period),
      gameTime: this.normalizeNullableText(row.gameTime),
      errorType: this.normalizeNullableOption(row.errorType),
      collector: this.normalizeNullableText(row.collector),
      qc: this.normalizeNullableText(row.qc),
      comment: this.normalizeNullableText(row.comment)
    };
  }

  private normalizeNullableOption<T extends string>(
    value: T | null | undefined
  ): T | null {
    if (typeof value !== 'string' || value === '') {
      return null;
    }

    return value;
  }

  private normalizeNullableText(value: string | null | undefined): string | null {
    return value === '' || value == null ? null : value;
  }

  private hasNonBlankText(value: string | null | undefined): boolean {
    return !!value?.trim();
  }
}
